// Shared presentation helpers for the Finance app.
import type { ComposerTranslation } from 'vue-i18n'

const PREFIX = 'finance'

const PAYMENT_METHOD_KEYS: Record<string, string> = {
  cash: 'paymentMethod.cash',
  card: 'paymentMethod.card',
  transfer: 'paymentMethod.transfer',
}

const PAYMENT_DIRECTION_KEYS: Record<string, string> = {
  inflow: 'paymentDirection.inflow',
  outflow: 'paymentDirection.outflow',
}

const INVOICE_STATUS_KEYS: Record<string, string> = {
  draft: 'invoiceStatus.draft',
  unpaid: 'invoiceStatus.unpaid',
  partial: 'invoiceStatus.partial',
  paid: 'invoiceStatus.paid',
}

const SUPPLIER_INVOICE_STATUS_KEYS: Record<string, string> = {
  open: 'supplierInvoiceStatus.open',
  partial: 'supplierInvoiceStatus.partial',
  closed: 'supplierInvoiceStatus.closed',
  void: 'supplierInvoiceStatus.void',
  overdue: 'supplierInvoiceStatus.overdue',
}

const ACCOUNT_TYPE_KEYS: Record<string, string> = {
  cashbox: 'accountType.cashbox',
  bank: 'accountType.bank',
  card: 'accountType.card',
  clearing: 'accountType.clearing',
}

const ADJUSTMENT_TYPE_KEYS: Record<string, string> = {
  customer_credit: 'adjustmentType.customer_credit',
  write_off: 'adjustmentType.write_off',
  debit_note: 'adjustmentType.debit_note',
  refund: 'adjustmentType.refund',
}

const ADJUSTMENT_STATUS_KEYS: Record<string, string> = {
  draft: 'adjustmentStatus.draft',
  posted: 'adjustmentStatus.posted',
  reversed: 'adjustmentStatus.reversed',
}

const TRANSFER_STATUS_KEYS: Record<string, string> = {
  posted: 'transferStatus.posted',
  reversed: 'transferStatus.reversed',
}

const ENTRY_KIND_KEYS: Record<string, string> = {
  payment: 'entryKind.payment',
  payment_reversal: 'entryKind.payment_reversal',
  transfer: 'entryKind.transfer',
  transfer_reversal: 'entryKind.transfer_reversal',
  transfer_fee: 'entryKind.transfer_fee',
  transfer_fee_reversal: 'entryKind.transfer_fee_reversal',
  adjustment: 'entryKind.adjustment',
}

const AGING_BUCKET_KEYS: Record<string, string> = {
  current: 'agingBucket.current',
  '1_30': 'agingBucket.days1_30',
  '31_60': 'agingBucket.days31_60',
  '60_plus': 'agingBucket.days60Plus',
}

function resolve(
  t: ComposerTranslation,
  keys: Record<string, string>,
  value: string | null | undefined,
): string {
  if (!value) return '—'
  const key = keys[value]
  if (!key) return value
  const label = t(`${PREFIX}.${key}`)
  return label && !String(label).startsWith(`${PREFIX}.`) ? label : value
}

export function paymentMethodLabel(t: ComposerTranslation, value?: string | null): string {
  return resolve(t, PAYMENT_METHOD_KEYS, value)
}

export function paymentDirectionLabel(t: ComposerTranslation, value?: string | null): string {
  return resolve(t, PAYMENT_DIRECTION_KEYS, value)
}

export function invoiceStatusLabel(t: ComposerTranslation, value?: string | null): string {
  return resolve(t, INVOICE_STATUS_KEYS, value)
}

export function supplierInvoiceStatusLabel(t: ComposerTranslation, value?: string | null): string {
  return resolve(t, SUPPLIER_INVOICE_STATUS_KEYS, value)
}

export function accountTypeLabel(t: ComposerTranslation, value?: string | null): string {
  return resolve(t, ACCOUNT_TYPE_KEYS, value)
}

export function adjustmentTypeLabel(t: ComposerTranslation, value?: string | null): string {
  return resolve(t, ADJUSTMENT_TYPE_KEYS, value)
}

export function adjustmentStatusLabel(t: ComposerTranslation, value?: string | null): string {
  return resolve(t, ADJUSTMENT_STATUS_KEYS, value)
}

export function transferStatusLabel(t: ComposerTranslation, value?: string | null): string {
  return resolve(t, TRANSFER_STATUS_KEYS, value)
}

export function entryKindLabel(t: ComposerTranslation, value?: string | null): string {
  return resolve(t, ENTRY_KIND_KEYS, value)
}

export function agingBucketLabel(t: ComposerTranslation, value?: string | null): string {
  return resolve(t, AGING_BUCKET_KEYS, value)
}

/** Status badge class for invoice-like lifecycle values. */
export function invoiceStatusClass(status?: string | null): string {
  switch (status) {
    case 'paid':
    case 'closed':
      return 'status-ok'
    case 'partial':
      return 'status-warn'
    case 'unpaid':
    case 'open':
    case 'overdue':
      return 'status-critical'
    case 'draft':
      return 'status-neutral'
    case 'void':
      return 'status-neutral'
    default:
      return 'status-neutral'
  }
}

export function adjustmentStatusClass(status?: string | null): string {
  switch (status) {
    case 'posted':
      return 'status-ok'
    case 'draft':
      return 'status-warn'
    case 'reversed':
      return 'status-critical'
    default:
      return 'status-neutral'
  }
}

export function transferStatusClass(status?: string | null): string {
  return status === 'reversed' ? 'status-critical' : 'status-ok'
}

export function riskClass(value: boolean | null | undefined): string {
  if (value === true) return 'status-ok'
  if (value === false) return 'status-critical'
  return 'status-neutral'
}

/** Grouped number with fixed decimals; the currency code is rendered separately. */
export function formatAmount(
  value: number | string | null | undefined,
  decimals = 2,
): string {
  const numeric = typeof value === 'string' ? Number(value) : value
  if (numeric === null || numeric === undefined || Number.isNaN(numeric)) return '—'
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(numeric)
}

/** Quantities and counts, without forced decimals. */
export function formatCount(value: number | string | null | undefined): string {
  const numeric = typeof value === 'string' ? Number(value) : value
  if (numeric === null || numeric === undefined || Number.isNaN(numeric)) return '—'
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 3 }).format(numeric)
}

/** Collection rates / ratios arrive either as 0..1 or already as a percentage. */
export function formatRate(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—'
  const percent = value <= 1 ? value * 100 : value
  return `${percent.toFixed(1)}%`
}

export function formatMoney(
  value: number | string | null | undefined,
  currency?: string | null,
  decimals = 2,
): string {
  const amount = formatAmount(value, decimals)
  return currency ? `${amount} ${currency}` : amount
}

/** ISO date (YYYY-MM-DD) for date inputs and API params. */
export function toIsoDate(value: Date = new Date()): string {
  const pad = (input: number) => String(input).padStart(2, '0')
  return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`
}

export function firstDayOfMonth(value: Date = new Date()): string {
  const pad = (input: number) => String(input).padStart(2, '0')
  return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-01`
}

export function initials(value: string | null | undefined): string {
  if (!value) return '—'
  const parts = value.trim().split(/\s+/).slice(0, 2)
  return parts.map((part) => part.charAt(0).toUpperCase()).join('') || '—'
}
